import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { dashboardAPI, leaveAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatDate, capitalizeFirst } from '../lib/utils';
import { Users, Wallet, CalendarDays, Building2, TrendingUp, Clock, CheckCircle, XCircle, FileText, UserPlus, Receipt, Calculator, RefreshCw, Database, Upload, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const REFRESH_INTERVAL = 30000; // 30 seconds

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importDialog, setImportDialog] = useState({ open: false, loading: false });

  const fetchImportStatus = useCallback(async () => {
    try {
      const res = await api.get('/import-status');
      setImportStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch import status');
    }
  }, []);

  const handleDataImport = async () => {
    setImportDialog(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.post('/import-employee-data?clear_existing=true');
      toast.success(`Import complete: ${res.data.results?.imported?.employees_created || 0} employees imported`);
      setImportDialog({ open: false, loading: false });
      fetchDashboardData(true);
      fetchImportStatus();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Import failed');
      setImportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchDashboardData = useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const response = await dashboardAPI.getAdmin();
      setData(response.data);
      setLastRefresh(new Date());
      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to refresh dashboard');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchImportStatus();
    
    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchDashboardData();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [fetchDashboardData, fetchImportStatus]);

  const handleLeaveAction = async (leaveId, action) => {
    try {
      await leaveAPI.processAction(leaveId, { action });
      toast.success(`Leave ${action}d successfully`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to process leave');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  // Overview stats (non-clickable)
  const overviewStats = [
    {
      label: 'Total Employees',
      value: data?.total_employees || 0,
      icon: Users,
      accent: 'bg-slate-50 border-slate-200',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      color: 'text-slate-900',
    },
    {
      label: 'Departments',
      value: data?.total_departments || 0,
      icon: Building2,
      accent: 'bg-slate-50 border-slate-200',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      color: 'text-slate-900',
    },
    {
      label: 'Monthly Salary Expense',
      value: formatCurrency(data?.monthly_salary_expense || 0),
      icon: Wallet,
      accent: 'bg-slate-50 border-slate-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      color: 'text-emerald-600',
    },
  ];

  // Pendency cards (clickable)
  const pendencyStats = [
    {
      label: 'Pending Leaves',
      value: data?.pending_leaves || 0,
      icon: CalendarDays,
      accent: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      color: 'text-amber-600',
      route: '/leave-management',
      testId: 'pending-leaves',
    },
    {
      label: 'Pending Registrations',
      value: data?.pending_registrations || 0,
      icon: UserPlus,
      accent: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      color: 'text-blue-600',
      route: '/employee-master?tab=registrations',
      testId: 'pending-registrations',
    },
    {
      label: 'Tax Declarations',
      value: data?.pending_tax_declarations || 0,
      icon: Calculator,
      accent: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      color: 'text-purple-600',
      route: '/tax-declarations',
      testId: 'pending-tax',
    },
    {
      label: 'Pending Claims',
      value: data?.pending_claims || 0,
      icon: Receipt,
      accent: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      color: 'text-orange-600',
      route: '/claims',
      testId: 'pending-claims',
    },
    {
      label: 'Salary Batches',
      value: data?.pending_salary_batches || 0,
      icon: FileText,
      accent: 'bg-teal-50 border-teal-200 hover:bg-teal-100',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      color: 'text-teal-600',
      route: '/salary',
      testId: 'pending-salary',
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <Header 
        title="Dashboard" 
        subtitle="Welcome to your HR management portal"
        action={
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchDashboardData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        }
      />
      
      <div className="p-8">
        {/* Overview Stats */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {overviewStats.map((stat) => (
              <div
                key={stat.label}
                className={`p-5 rounded-xl border ${stat.accent}`}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                      {stat.label}
                    </p>
                    <p className={`text-2xl font-heading font-semibold ${stat.color}`}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.iconBg}`}>
                    <stat.icon className={`w-5 h-5 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pendency Cards - Clickable */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">Pending Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {pendencyStats.map((stat) => (
              <div
                key={stat.label}
                className={`p-5 rounded-xl border cursor-pointer transition-all duration-200 ${stat.accent} ${stat.value > 0 ? 'ring-2 ring-offset-1' : ''}`}
                style={{ ringColor: stat.value > 0 ? 'inherit' : 'transparent' }}
                onClick={() => navigate(stat.route)}
                data-testid={stat.testId}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`p-3 rounded-lg ${stat.iconBg} mb-3`}>
                    <stat.icon className={`w-6 h-6 ${stat.iconColor}`} strokeWidth={1.5} />
                  </div>
                  <p className={`text-3xl font-heading font-bold ${stat.color}`}>
                    {stat.value}
                  </p>
                  <p className="text-xs font-medium text-slate-600 mt-1">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Leave Requests */}
          <div className="lg:col-span-2 dashboard-card" data-testid="pending-leaves-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Recent Leave Requests</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/leave-management')}>
                View All
              </Button>
            </div>
            
            {data?.recent_leaves?.length === 0 ? (
              <div className="empty-state py-8">
                <CalendarDays className="w-12 h-12 text-slate-300" />
                <h3 className="text-base font-medium text-slate-600">No pending requests</h3>
                <p className="text-sm text-slate-400">All leave requests have been processed</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data?.recent_leaves?.map((leave) => (
                  <div
                    key={leave.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`leave-request-${leave.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-amber-600">
                          {leave.employee_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{leave.employee_name}</p>
                        <p className="text-sm text-slate-500">
                          {capitalizeFirst(leave.leave_type)} Leave • {leave.days} day(s)
                        </p>
                        <p className="text-xs text-slate-400">
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                        onClick={() => handleLeaveAction(leave.id, 'approve')}
                        data-testid={`approve-leave-${leave.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleLeaveAction(leave.id, 'reject')}
                        data-testid={`reject-leave-${leave.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Department Distribution */}
          <div className="dashboard-card" data-testid="department-distribution">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-slate-900">By Department</h2>
              <TrendingUp className="w-5 h-5 text-slate-400" />
            </div>
            
            <div className="space-y-4">
              {data?.department_distribution?.map((dept, idx) => (
                <div key={dept.id || dept.name || `dept-${idx}`} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: `hsl(${idx * 60}, 60%, 50%)` }}
                    ></div>
                    <span className="text-sm text-slate-700">{dept.name}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900">{dept.count}</span>
                </div>
              ))}
            </div>

            {data?.department_distribution?.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No department data</p>
            )}
          </div>
        </div>

        {/* System Settings Section */}
        <div className="mt-8 p-6 bg-slate-50 rounded-xl border">
          <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">System Settings</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Employee Data Import</h3>
                <p className="text-sm text-slate-500">
                  {importStatus ? (
                    <>
                      {importStatus.total_employees} employees • {importStatus.insurance_policies} policies • 
                      <span className={importStatus.data_status === 'current' ? 'text-green-600' : 'text-amber-600'}>
                        {importStatus.data_status === 'current' ? ' Data current' : ' Needs import'}
                      </span>
                    </>
                  ) : 'Loading status...'}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setImportDialog({ open: true, loading: false })}
              variant={importStatus?.data_status === 'needs_import' ? 'default' : 'outline'}
              data-testid="import-data-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importStatus?.data_status === 'needs_import' ? 'Import Data' : 'Re-import Data'}
            </Button>
          </div>
        </div>
      </div>

      {/* Import Confirmation Dialog */}
      <Dialog open={importDialog.open} onOpenChange={(open) => !importDialog.loading && setImportDialog({ open, loading: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Import Employee Data
            </DialogTitle>
            <DialogDescription>
              This will import employee and insurance data from the uploaded Excel file.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Warning: This will clear existing data</p>
                <p className="text-amber-600 mt-1">
                  All existing employee records (except admin accounts) will be deleted and replaced with the new data.
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700">What will be imported:</p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1">
                <li>• 5,370 employees with login credentials</li>
                <li>• Employee ID = Login ID = Default Password</li>
                <li>• Insurance policies with health card info</li>
                <li>• First-time login will require password change</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setImportDialog({ open: false, loading: false })}
              disabled={importDialog.loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDataImport}
              disabled={importDialog.loading}
              data-testid="confirm-import-btn"
            >
              {importDialog.loading ? 'Importing...' : 'Confirm Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
